<template>
  <div ref="container" class="d-flex align-center overflow-hidden flex-nowrap min-w-0">
    <!-- Root -->
    <template v-if="itemsList.length">
      <template v-if="isSingleCurrentRoot">
        <AppTooltip :max-width="600" location="bottom">
          <template #activator="{ props }">
            <div class="py-1 px-3 min-w-0">
              <span
                v-bind="props"
                class="d-inline-block text-truncate text--bold"
                style="max-width: 100%;"
              >
                <span v-if="rootFolder?.numbering" class="pr-1">
                  {{ rootFolder.numbering }}
                </span>
                <span v-if="rootFolder?.name">
                  {{ rootFolder.name }}
                </span>
              </span>
            </div>
          </template>
          <span v-if="rootFolder?.numbering" class="pr-1">
            {{ rootFolder.numbering }}
          </span>
          <span v-if="rootFolder?.name">
            {{ rootFolder.name }}
          </span>
        </AppTooltip>
      </template>
      <template v-else>
        <span
          class="py-1 px-3"
          :class="isClickable(rootFolder) ? 'clickable' : ''"
          @click="onGoTo(rootFolder)"
        >
          <span v-if="rootFolder?.numbering" class="pr-1">{{ rootFolder.numbering }}</span>
          <span v-if="rootFolder?.name">{{ rootFolder.name }}</span>
        </span>
        <v-breadcrumbs-divider v-if="itemsList.length > 1">
          <font-awesome-icon :icon="['fal', 'chevron-right']" />
        </v-breadcrumbs-divider>
      </template>
    </template>

    <!-- Ellipsis -->
    <template v-if="hiddenFolders.length">
      <v-menu offset-y>
        <template #activator="{ props }">
          <span
            v-bind="props"
            class="ellipsis-menu clickable text--bold py-1 px-2"
            data-locator="picker-ellipsis-menu"
          >
            ...
          </span>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, i) in hiddenFolders"
            :key="'hidden-'+i"
            @click="onGoTo(item)"
          >
            <v-list-item-title>
              <span v-if="item.numbering" class="pr-1">{{ item.numbering }}</span>
              <span>{{ item.name }}</span>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-breadcrumbs-divider>
        <font-awesome-icon :icon="['fal', 'chevron-right']" />
      </v-breadcrumbs-divider>
    </template>

    <!-- Visible middle -->
    <template v-for="(item, i) in visibleFolders" :key="'visible-' + i">
      <AppTooltip :max-width="600" location="bottom">
        <template #activator="{ props }">
          <div class="py-1 px-3 min-w-0 clickable" @click="onGoTo(item)">
            <span
              v-bind="props"
              class="d-inline-block text-truncate"
              style="max-width: 100%;"
            >
              <span v-if="item.numbering" class="pr-1">{{ item.numbering }}</span>
              <span>{{ item.name }}</span>
            </span>
          </div>
        </template>
        <span v-if="item.numbering" class="pr-1">{{ item.numbering }}</span>
        <span>{{ item.name }}</span>
      </AppTooltip>

      <v-breadcrumbs-divider
        v-if="i < visibleFolders.length - 1 || (lastFolder && lastFolder !== rootFolder)"
      >
        <font-awesome-icon :icon="['fal', 'chevron-right']" />
      </v-breadcrumbs-divider>
    </template>

    <!-- Last Folder -->
    <template v-if="lastFolder && lastFolder !== rootFolder">
      <AppTooltip :max-width="600" location="bottom">
        <template #activator="{ props }">
          <div class="py-1 px-3 min-w-0">
            <span
              v-bind="props"
              class="d-inline-block text-truncate text--bold"
              style="max-width: 100%;"
            >
              <span v-if="lastFolder.numbering" class="pr-1">{{ lastFolder.numbering }}</span>
              <span>{{ lastFolder.name }}</span>
            </span>
          </div>
        </template>
        <span v-if="lastFolder.numbering" class="pr-1">{{ lastFolder.numbering }}</span>
        <span>{{ lastFolder.name }}</span>
      </AppTooltip>
    </template>

    <!-- Hidden measurement row -->
    <span
      ref="measureRow"
      style="position:absolute; left:-9999px; top:-9999px; visibility:hidden; white-space:nowrap;"
    >
      <template v-for="(item, i) in itemsList" :key="'measure-'+i">
        <span :ref="el => setMeasureFolderRef(el, i)" class="breadcrumb-measure-item py-1 px-3">
          <span v-if="item.numbering" class="pr-1">{{ item.numbering }}</span>
          <span>{{ item.name }}</span>
        </span>
      </template>
    </span>
  </div>
</template>

<script>
import AppTooltip from '@/common/AppTooltip.vue'

export default {
  name: 'DocumentsPickerBreadcrumb',
  components: { AppTooltip },
  props: {
    breadcrumb: {
      type: Array,
      required: true,
    },
    currentFolderId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ['go-to'],
  data () {
    return {
      visibleFolders: [],
      hiddenFolders: [],
      measureFolderRefs: [],
      resizeObserver: null,
    }
  },
  computed: {
    itemsList () {
      return this.breadcrumb || []
    },
    rootFolder () {
      return this.itemsList.length ? this.itemsList[0] : null
    },
    lastFolder () {
      return this.itemsList.length ? this.itemsList[this.itemsList.length - 1] : null
    },
    isSingleCurrentRoot () {
      return this.itemsList.length === 1
    },
  },
  watch: {
    breadcrumb: {
      async handler () {
        await this.$nextTick()
        this.calculateVisibleFolders()
      },
      deep: true,
      immediate: true,
    },
  },
  mounted () {
    if (this.$refs.container) {
      this.resizeObserver = new globalThis.ResizeObserver(() => this.calculateVisibleFolders())
      this.resizeObserver.observe(this.$refs.container)
    }
  },
  beforeUnmount () {
    if (this.resizeObserver && this.$refs.container) {
      this.resizeObserver.unobserve(this.$refs.container)
      this.resizeObserver.disconnect()
    }
  },
  methods: {
    setMeasureFolderRef (el, i) {
      if (el) this.measureFolderRefs[i] = el
    },
    isClickable (item) {
      const idA = String(item?.id)
      const idB = this.currentFolderId == null ? null : String(this.currentFolderId)
      return idB == null ? true : idA !== idB
    },
    onGoTo (item) {
      if (!item) return
      if (!this.isClickable(item)) return
      this.$emit('go-to', item)
    },
    async calculateVisibleFolders () {
      this.measureFolderRefs = []
      await this.$nextTick()

      const container = this.$refs.container
      const items = this.itemsList
      if (!container || !items?.length) {
        this.visibleFolders = []
        this.hiddenFolders = []
        return
      }
      if (items.length <= 2) {
        this.visibleFolders = []
        this.hiddenFolders = []
        return
      }

      const containerWidth = container.offsetWidth
      const ellipsisWidth = 50
      const dividerWidth = 30
      const middleItems = items.slice(1, -1)
      const rootEl = this.measureFolderRefs[0]
      const lastEl = this.measureFolderRefs[items.length - 1]

      let mandatoryWidth = 0
      if (rootEl) mandatoryWidth += rootEl.offsetWidth + dividerWidth
      if (lastEl) mandatoryWidth += lastEl.offsetWidth

      // Fit all without ellipsis
      let totalWidth = mandatoryWidth
      for (let i = 0; i < middleItems.length; i++) {
        const el = this.measureFolderRefs[i + 1]
        if (el) totalWidth += el.offsetWidth + dividerWidth
      }

      if (totalWidth <= containerWidth) {
        this.visibleFolders = middleItems
        this.hiddenFolders = []
        return
      }

      // Need ellipsis, fit as many as possible from the end
      let currentWidth = mandatoryWidth + ellipsisWidth + dividerWidth
      const visible = []
      let hidden = []

      for (let i = middleItems.length - 1; i >= 0; i--) {
        const el = this.measureFolderRefs[i + 1]
        if (!el) break

        const itemWidth = el.offsetWidth + dividerWidth
        if (currentWidth + itemWidth <= containerWidth) {
          visible.unshift(middleItems[i])
          currentWidth += itemWidth
        } else {
          hidden = middleItems.slice(0, i + 1)
          break
        }
      }

      this.visibleFolders = visible
      this.hiddenFolders = hidden
    },
  },
}
</script>

<style scoped>
.ellipsis-menu {
  user-select: none;
}
.ellipsis-menu:hover,
.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}
</style>
